import { Button } from "@svea/design-system-components-react";
import {
	AriaPopover,
	ButtonGroup,
	formatPhone,
	Grid,
	Heading,
	isRequired,
	Markdown,
	ReactSelectField,
	Stack,
	Text,
	TextAreaField,
	TextField,
	TextInput,
	useText,
	validateClcZipCode,
	validateEmail,
	validateMaxLength,
	validatePhoneNumber,
} from "@sveabank/cc-common";
import arrayMutators from "final-form-arrays";
import { Field, Form } from "react-final-form";
import { usePageContext } from "../../../../hooks/context";
import type { ClcCompanyRegistrationForm } from "../../../../types/clc-application";
import { useClcCompanyApplication } from "../../hooks";
import { NameSuggestionsField } from "./name-suggestions";
import { SniCodesField } from "./sni-codes";

type Props = {
	onSubmit: (data: ClcCompanyRegistrationForm) => Promise<void>;
	onCancel: () => void;
};

export const ClcCompanyInfo = ({ onSubmit, onCancel }: Props) => {
	const [t, texts] = useText();
	const {
		allSniCodes,
		clcApplicationDetails: {
			companyRegistration: { value: companyRegistrationValues },
			applicant,
		},
		countryCodes,
		isPostingClcCompanyRegistration,
	} = usePageContext(useClcCompanyApplication);

	const validateConfirmEmail = (values: Partial<ClcCompanyRegistrationForm>) => {
		if (values.confirmEmail !== values.applicantEmail)
			return {
				confirmEmail: texts.validation.emailDoesNotMatch,
			};
		return {};
	};

	return (
		<Grid>
			<Grid.Col
				span={{
					base: 12,
					tablet: 8,
				}}
			>
				<Form<ClcCompanyRegistrationForm>
					validate={validateConfirmEmail}
					mutators={{
						...arrayMutators,
					}}
					onSubmit={(values) => {
						const formattedValues: ClcCompanyRegistrationForm = {
							...values,
							applicantPhone: {
								countryCode: values.applicantPhone?.countryCode || "",
								number: formatPhone(values.applicantPhone?.number || ""),
							},
							companyAddress: {
								...values.companyAddress,
								zipCode: values.companyAddress.zipCode.replace(/ /g, ""),
							},
						};
						onSubmit(formattedValues);
					}}
					render={({
						form: {
							mutators: { push },
						},
						handleSubmit,
					}) => {
						return (
							<form onSubmit={handleSubmit} name="companyRegistrationForm">
								<Stack gap={8}>
									<Stack gap={2} align="start">
										<Stack direction="row" gap={1}>
											<Heading.H3 size="h4" italic={false}>
												{t(texts.newCompany.companyInfo.addressHeading)}
											</Heading.H3>
											<AriaPopover
												variant="info"
												content={() => (
													<Markdown>{t(texts.newCompany.companyInfo.helpText1)}</Markdown>
												)}
											/>
										</Stack>
										<Stack gap={4} align="start" fullSize>
											<TextField
												fullWidth
												label={t(texts.newCompany.companyInfo.coAddress)}
												initialValue={companyRegistrationValues?.companyAddress?.coAddress}
												name="companyAddress.coAddress"
											/>
											<TextField
												fullWidth
												label={t(texts.newCompany.companyInfo.address)}
												name="companyAddress.addressLine"
												initialValue={companyRegistrationValues?.companyAddress?.addressLine}
												validators={[isRequired]}
											/>
											<TextField
												fullWidth
												label={t(texts.newCompany.companyInfo.zip)}
												name="companyAddress.zipCode"
												initialValue={companyRegistrationValues?.companyAddress?.zipCode}
												validators={[isRequired, validateClcZipCode]}
											/>
											<TextField
												fullWidth
												label={t(texts.newCompany.companyInfo.city)}
												name="companyAddress.postalCity"
												initialValue={companyRegistrationValues?.companyAddress?.postalCity}
												validators={[isRequired]}
											/>
											<Field
												name="companyAddress.countryCode"
												initialValue="SE"
												render={() => <></>}
											/>
										</Stack>
									</Stack>
									<Stack gap={4} align="start">
										<Heading.H3 size="h4" italic={false}>
											{t(texts.newCompany.companyInfo.contactHeading)}
										</Heading.H3>
										<TextInput
											disabled
											fullWidth
											label={t(texts.newCompany.companyInfo.name)}
											name="applicantName"
											value={`${applicant.firstName} ${applicant.lastName}`}
										/>
										<TextField
											fullWidth
											label={t(texts.newCompany.companyInfo.email)}
											name="applicantEmail"
											initialValue={applicant.email || ""}
											validators={[isRequired, validateEmail]}
										/>
										<TextField
											fullWidth
											label={t(texts.newCompany.companyInfo.confirmEmail)}
											name="confirmEmail"
											initialValue={applicant.email || ""}
											validators={[isRequired, validateEmail]}
										/>

										<Grid>
											<Grid.Col span={{ base: 12, tablet: 4 }}>
												<ReactSelectField
													label={t(texts.contactInfo.companyContact.countryCode)}
													floatingLabel
													name="applicantPhone.countryCode"
													options={countryCodes}
													placeholder={t(texts.common.choose)}
													initialValue={applicant.phone?.countryCode || "+46"}
													validators={[isRequired]}
												/>
											</Grid.Col>
											<Grid.Col span={{ base: 12, tablet: 8 }}>
												<TextField
													fullWidth
													label={t(texts.newCompany.companyInfo.phone)}
													name="applicantPhone.number"
													initialValue={applicant.phone?.number}
													validators={[isRequired, validatePhoneNumber]}
												/>
											</Grid.Col>
										</Grid>
									</Stack>
									<Stack gap={2} align="start">
										<Stack direction="row" gap={1}>
											<Heading.H3 size="h4" italic={false}>
												{t(texts.newCompany.companyInfo.nameSuggestions)}
											</Heading.H3>

											<AriaPopover
												variant="info"
												content={() => (
													<Markdown>{t(texts.newCompany.companyInfo.helpText2)}</Markdown>
												)}
											/>
										</Stack>
										<NameSuggestionsField
											onAddNameSuggestion={() => push?.("proposedCompanyNames", "")}
											initialValues={companyRegistrationValues?.proposedCompanyNames}
										/>
									</Stack>
									<Stack gap={2} align="start">
										<Stack direction="row" gap={1}>
											<Heading.H3 size="h4" italic={false}>
												{t(texts.newCompany.companyInfo.businessDescription)}
											</Heading.H3>
											<AriaPopover
												variant="info"
												content={() => (
													<Markdown>{t(texts.newCompany.companyInfo.helpText3)}</Markdown>
												)}
											/>
										</Stack>
										<TextAreaField
											label={t(texts.newCompany.companyInfo.businessDescription)}
											placeholder={t(texts.newCompany.companyInfo.businessDescriptionText)}
											name="businessDescription"
											rows={3}
											initialValue={companyRegistrationValues?.businessDescription}
											validators={[isRequired, validateMaxLength(1000)]}
											maxLength={1000}
										/>
									</Stack>
									<Stack gap={2}>
										<Stack direction="row" gap={1}>
											<Heading.H3 size="h4" italic={false}>
												{t(texts.newCompany.companyInfo.sni)}
											</Heading.H3>
											<AriaPopover
												variant="info"
												content={() => (
													<Markdown allowParagraphs>
														{t(texts.newCompany.companyInfo.helpText4)}
													</Markdown>
												)}
											/>
										</Stack>
										<Stack gap={4}>
											<Text>{t(texts.newCompany.companyInfo.sniText)}</Text>
											<SniCodesField
												allSniCodes={allSniCodes}
												initialValues={companyRegistrationValues?.sniCodes}
											/>
										</Stack>
									</Stack>
									<ButtonGroup row align="start">
										<Button type="button" variant="secondary" onPress={onCancel}>
											{t(texts.common.abort)}
										</Button>
										<Button
											type="submit"
											isPending={isPostingClcCompanyRegistration}
											data-tracking-action="CLC Application Continue"
											data-tracking-name="Company Information"
										>
											{t(texts.common.continue)}
										</Button>
									</ButtonGroup>
								</Stack>
							</form>
						);
					}}
				/>
			</Grid.Col>
		</Grid>
	);
};
